import {
  LineSeriesData,
  StatisticsItemProps,
} from '$core/Statistics/StatisticsItem/StatisticsItem.interface';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { Collapse } from '$uikit';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  STATISTICS_CHART_CONFIG,
  STATISTICS_LINES_COLORS,
} from '$shared/constants/statistics';
import { useDispatch, useSelector } from 'react-redux';
import {
  statisticsActions,
  statisticsContentSelector,
  statisticsSelector,
} from '$store/statistics';
import { RootState } from '$store/rootReducer';
import {
  CurrencyEnum,
  StatisticsContentModel,
  StatisticsEarningsModel,
  StatisticsEarningsTypes,
} from '$store/models';
import './StatisticsItem.sass';
import { Currency, Loader } from '$shared/components';
import { useTranslator } from '$hooks';
import { classNames, format } from '$utils';
import { fromUnixTime } from 'date-fns';
import invert from 'lodash/invert';

enum EurEarnings {
  SPONSORS = 'sponsors',
  MESSAGES = 'messages',
  TIPS = 'tips',
  BUNDLES = 'bundles',
}

enum TonEarnings {
  MEMBERSHIP = 'membership',
  REFERRALS = 'referrals',
  STAKING = 'staking',
}

const CURRENCY_EARNINGS_TYPES = {
  [CurrencyEnum.EUR]: EurEarnings,
  [CurrencyEnum.TON]: TonEarnings,
};

interface Earnings {
  [date: number]: StatisticsEarningsModel | {};
}

export const StatisticsItem = ({
  date,
  totalAmount,
  currentCurrency,
}: StatisticsItemProps) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { shownItemData } = useSelector(statisticsSelector);
  const content: StatisticsContentModel | undefined = useSelector((state: RootState) =>
    statisticsContentSelector(state, date),
  );

  const calculatedTotalAmount = useMemo((): number => {
    if (totalAmount !== undefined) {
      return totalAmount;
    } else if (content) {
      const lastDay = Object.keys(content.earnings).reduce(
        (a, b) => Math.max(a, Number(b)),
        0,
      );
      const stats = content.earnings[lastDay][currentCurrency];
      return (
        currentCurrency === CurrencyEnum.EUR
          ? [stats.sponsors, stats.messages, stats.tips, stats.bundles]
          : [stats.membership, stats.referrals, stats.staking]
      ).reduce((a, b) => a + b, 0);
    }
    return 0;
  }, [totalAmount, content, currentCurrency]);

  const isLoading = content ? content.isLoading : false;
  const shownItemId = shownItemData[currentCurrency];
  const isShown = shownItemId === date;
  const formattedDate = fromUnixTime(date);
  const isNow = new Date().getMonth() === formattedDate.getMonth();

  useEffect(() => {
    const chartBackground = document.querySelector('.highcharts-plot-background');

    if (chartBackground) {
      chartBackground.setAttribute('rx', '8');
    }
  }, [isLoading, isShown]);

  const itemTitle = useMemo(() => {
    if (!date) {
      return t('statistics_date_all');
    }
    let month = format(formattedDate, 'LLLL');
    month = `${month[0].toUpperCase()}${month.slice(1)}`;
    return isNow ? `${month} (${t('statistics_date_now')})` : month;
  }, [isNow, formattedDate, date, t]);

  const currentEarnings = useMemo(() => {
    if (!content?.earnings) return null;

    const currencyEarnings: Earnings = {};

    for (const date in content.earnings) {
      const currentCurrencyEarning = content.earnings[date][currentCurrency];

      if (currentCurrencyEarning) {
        currencyEarnings[date] = currentCurrencyEarning;
      }
    }

    const filteredEarnings: Earnings = {};
    const currentEarningsTypes = invert(CURRENCY_EARNINGS_TYPES[currentCurrency]);

    for (let date in currencyEarnings) {
      filteredEarnings[date] = {};

      for (let earningType in currencyEarnings[date]) {
        if (earningType in currentEarningsTypes) {
          filteredEarnings[date][earningType] = currencyEarnings[date][earningType];
        }
      }
    }

    return filteredEarnings;
  }, [content?.earnings, currentCurrency]);

  const earningsFormatted = useMemo(() => {
    if (!currentEarnings) return [];

    const lastDay = Math.max(...Object.keys(currentEarnings).map(parseFloat));
    return Object.entries(currentEarnings[lastDay]).map(([name, amount]) => ({
      name,
      amount,
    }));
  }, [currentEarnings]);

  const maxStatsValue = earningsFormatted.length
    ? Math.max(...earningsFormatted.map((e) => e.amount))
    : 0;

  const chartSeries = useMemo(() => {
    if (!currentEarnings) return null;

    const data = Object.values(currentEarnings).reduce(
      (seriesData: LineSeriesData, earningsInfo) => {
        for (const earningsElement in earningsInfo) {
          if (earningsElement in seriesData) {
            seriesData[earningsElement].data.push(earningsInfo[earningsElement]);
          } else {
            seriesData[earningsElement] = {
              name: earningsElement,
              type: 'spline',
              data: [earningsInfo[earningsElement]],
              color: {
                linearGradient: { x1: 0, x2: 1, y1: 0, y2: 0 },
                stops: STATISTICS_LINES_COLORS[earningsElement],
              },
            };
          }
        }

        return seriesData;
      },
      {},
    );

    return Object.values(data);
  }, [currentEarnings]);

  const chartCategories = useMemo(() => {
    if (!currentEarnings) return [];

    return Object.keys(currentEarnings).map((date) =>
      format(fromUnixTime(+date), 'd MMM'),
    );
  }, [currentEarnings]);

  const chartOptions = useMemo(() => {
    const defaultOptions = { ...STATISTICS_CHART_CONFIG };

    if (defaultOptions && defaultOptions.xAxis && chartSeries) {
      defaultOptions.series = chartSeries;
      defaultOptions.xAxis = {
        ...defaultOptions.xAxis,
        categories: chartCategories,
      };
      defaultOptions.yAxis = {
        ...defaultOptions.yAxis,
        min: -(maxStatsValue / 10),
      };

      if (
        defaultOptions.plotOptions &&
        defaultOptions.plotOptions.spline &&
        defaultOptions.plotOptions.spline.marker
      ) {
        defaultOptions.plotOptions.spline.marker.enabled = chartCategories?.length === 1;
      }
    }
    return defaultOptions;
  }, [chartSeries, chartCategories, maxStatsValue]);

  const openItem = () => {
    if (content) {
      dispatch(
        statisticsActions.openStatisticsContent({
          date,
          currency: currentCurrency,
        }),
      );
    } else {
      dispatch(
        statisticsActions.loadStatisticsContent({
          date,
          currency: currentCurrency,
        }),
      );
    }
  };

  const closeItem = () => {
    dispatch(statisticsActions.closeStatisticsContent(currentCurrency));
  };

  const getEarningName = (earningType: string) => {
    switch (earningType) {
      case StatisticsEarningsTypes.MEMBERSHIP:
        return t('membership');

      case StatisticsEarningsTypes.STAKING:
        return t('staking');

      case StatisticsEarningsTypes.REFERRALS:
        return t('referrals');

      case StatisticsEarningsTypes.SPONSORS:
        return t('sponsors');

      case StatisticsEarningsTypes.MESSAGES:
        return t('messages');

      case StatisticsEarningsTypes.TIPS:
        return t('tips');

      case StatisticsEarningsTypes.BUNDLES:
        return t('bundles');
    }
  };

  return (
    <Collapse
      title={itemTitle}
      subTitle={
        <Currency amount={calculatedTotalAmount} currency={currentCurrency} precise={2} />
      }
      onClick={isShown ? closeItem : openItem}
      isOpen={isShown}
      className="StatisticsItem"
    >
      {isShown && isLoading && <Loader isWrapped />}
      {isShown && !isLoading && (
        <>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          <ul className="StatisticsItem__legend">
            {earningsFormatted.map((earning) => {
              return (
                <li key={earning.name} className="StatisticsItem__legend__item">
                  <span className={`StatisticsItem__legend__item_name ${earning.name}`}>
                    {getEarningName(earning.name)}
                  </span>
                  <span className="StatisticsItem__legend__item_amount">
                    <Currency
                      amount={earning.amount as number}
                      currency={currentCurrency}
                      precise={2}
                    />
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Collapse>
  );
};
